import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { Card } from '../components/Card'
import { Toggle } from '../components/Toggle'
import { ToggleRow } from '../components/ToggleRow'
import { InfoTooltip } from '../components/InfoTooltip'
import type { GuardrailFormState } from '../lib/types'
import { FEATURES } from '../lib/features'
import { NotSubscribedCard } from './PreviewPage'

const BCP47_RE = /^[a-zA-Z]{2,3}(-[a-zA-Z]{4})?(-(?:[a-zA-Z]{2}|\d{3}))?$/

/* ─── Preview card (MS-866 AC1/AC2) ───
   Rendered instead of the full config UI on Global Settings while
   FEATURES.globalGuardrailsConfig is false. The legacy UI is retained
   (hide-not-delete) — see ConnectionsPreview in GlobalConnectionsTab.tsx for
   the precedent. Agent-level Guardrails (AgentDetail.tsx) is unaffected —
   this wrapper is only used by GlobalSettings.tsx's guardrails panel. */

export function GlobalGuardrailsPreview() {
  const { t } = useTranslation()
  return (
    <NotSubscribedCard
      cardTitle={t('globalGuardrails.preview.cardTitle')}
      description={t('globalGuardrails.preview.description')}
      voteFeature="Global Guardrails"
    />
  )
}

/* ─── Card wrappers ─── */


function SectionHeading({ label }: { label: string }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--text-tertiary)', marginBottom: 8 }}>
      {label}
    </div>
  )
}

function Divider() {
  return <div style={{ borderTop: '1px solid var(--border-light)', margin: '12px 0' }} />
}

/* ─── Guardrails tab ─── */

export interface GuardrailsTabProps {
  form: GuardrailFormState
  onChange: (patch: Partial<GuardrailFormState>) => void
}

const selectStyle: React.CSSProperties = {
  height: 28,
  padding: '0 8px',
  fontSize: 13,
  border: '1px solid var(--border)',
  borderRadius: 6,
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
}

export function GuardrailsTab({ form, onChange }: GuardrailsTabProps) {
  const { t } = useTranslation()
  const [dryRunConfirm, setDryRunConfirm] = useState(false)

  // CURATED_LANGUAGES uses t() so must be inside the component via useMemo
  const CURATED_LANGUAGES = useMemo(() => [
    { value: 'und', label: t('guardrailsTab.languages.matchTenant') },
    { value: 'en',  label: t('guardrailsTab.languages.en') },
    { value: 'de',  label: t('guardrailsTab.languages.de') },
    { value: 'es',  label: t('guardrailsTab.languages.es') },
    { value: 'fr',  label: t('guardrailsTab.languages.fr') },
    { value: 'it',  label: t('guardrailsTab.languages.it') },
    { value: 'pt',  label: t('guardrailsTab.languages.pt') },
  ], [t])

  // CURRENCIES are simple strings — no t() needed but kept local for consistency
  const CURRENCIES = useMemo(() => ['EUR', 'USD', 'GBP'], [])

  const isCuratedLang = CURATED_LANGUAGES.some(l => l.value === form.responseLanguage)
  const [showCustomInput, setShowCustomInput] = useState(!isCuratedLang)
  const [localCustomLang, setLocalCustomLang] = useState(() =>
    isCuratedLang ? '' : form.responseLanguage
  )
  const [customLangError, setCustomLangError] = useState<string | null>(null)

  const langSelectValue = showCustomInput ? '_other' : form.responseLanguage

  const monetaryPairError =
    form.monetaryApprovalThreshold.trim() !== '' && !form.monetaryApprovalCurrency
      ? t('guardrailsTab.errors.currencyRequired')
      : null

  const monetaryNegativeError =
    form.monetaryApprovalThreshold.trim() !== '' &&
    parseFloat(form.monetaryApprovalThreshold) < 0
      ? t('guardrailsTab.errors.negativeThreshold')
      : null

  const [sessionTimeoutError, setSessionTimeoutError] = useState<string | null>(null)

  function handleDryRunToggle(value: boolean) {
    if (!value) {
      setDryRunConfirm(true)
    } else {
      onChange({ dryRunMode: true })
    }
  }

  function handleLangSelect(v: string) {
    if (v === '_other') {
      setShowCustomInput(true)
      if (localCustomLang) onChange({ responseLanguage: localCustomLang })
    } else {
      setShowCustomInput(false)
      setLocalCustomLang('')
      setCustomLangError(null)
      onChange({ responseLanguage: v })
    }
  }

  function handleCustomLangChange(raw: string) {
    setLocalCustomLang(raw)
    const trimmed = raw.trim()
    if (!trimmed) {
      setCustomLangError(t('guardrailsTab.errors.languageTagRequired'))
      return
    }
    if (!BCP47_RE.test(trimmed)) {
      setCustomLangError(t('guardrailsTab.errors.invalidBcp47'))
    } else {
      setCustomLangError(null)
    }
    onChange({ responseLanguage: trimmed })
  }

  return (
    <>
      {/* Action Controls — hidden for MVP (MS-562); set FEATURES.guardrailsActionControls = true to re-enable */}
      {FEATURES.guardrailsActionControls && <Card title={t('guardrailsTab.actionControls.title')} subtitle={t('guardrailsTab.actionControls.subtitle')} className="mb-16">
          <SectionHeading label={t('guardrailsTab.sections.approvals')} />

          <ToggleRow
            checked={form.requireHumanConfirmation}
            onChange={(v) => onChange({ requireHumanConfirmation: v })}
            label={t('guardrailsTab.toggles.requireHumanConfirmation')}
          />
          <ToggleRow
            checked={form.escalateOnUncertainty}
            onChange={(v) => onChange({ escalateOnUncertainty: v })}
            label={t('guardrailsTab.toggles.escalateToHuman')}
          />

          {/* Monetary threshold for human approval */}
          <div className="guardrail-number">
            <label htmlFor="monetary-threshold">
              {t('guardrailsTab.fields.monetaryThreshold')}
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <select
                aria-label={t('guardrailsTab.fields.approvalCurrencyAriaLabel')}
                value={form.monetaryApprovalCurrency}
                onChange={(e) => onChange({ monetaryApprovalCurrency: e.target.value })}
                style={{ ...selectStyle, width: 80 }}
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="">—</option>
              </select>
              <input
                id="monetary-threshold"
                type="number"
                min={0}
                max={99999999}
                value={form.monetaryApprovalThreshold}
                placeholder={t('guardrailsTab.fields.noneThreshold')}
                onChange={(e) => onChange({ monetaryApprovalThreshold: e.target.value })}
              />
            </div>
          </div>
          {monetaryPairError && (
            <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2, marginBottom: 4 }}>
              {monetaryPairError}
            </div>
          )}
          {monetaryNegativeError && (
            <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2, marginBottom: 4 }}>
              {monetaryNegativeError}
            </div>
          )}

          <Divider />
          <SectionHeading label={t('guardrailsTab.sections.permissions')} />

          <ToggleRow
            checked={form.allowBulkOperations}
            onChange={(v) => onChange({ allowBulkOperations: v })}
            label={t('guardrailsTab.toggles.allowBulkOperations')}
          />
          <ToggleRow
            checked={form.allowOutOfHoursActions}
            onChange={(v) => onChange({ allowOutOfHoursActions: v })}
            label={t('guardrailsTab.toggles.allowOutsideHours')}
          />
          <ToggleRow
            checked={form.dryRunMode}
            onChange={handleDryRunToggle}
            label={t('guardrailsTab.toggles.enableDryRun')}
          />

          <Divider />
          <SectionHeading label={t('guardrailsTab.sections.limits')} />

          <div className="guardrail-number">
            <label htmlFor="session-timeout">
              {t('guardrailsTab.fields.sessionTimeout')}
            </label>
            <input
              id="session-timeout"
              type="number"
              min={1}
              max={1440}
              value={form.sessionTimeoutMinutes}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10)
                if (!isNaN(v)) {
                  if (v < 1 || v > 1440) {
                    setSessionTimeoutError(t('guardrailsTab.errors.sessionTimeoutBounds'))
                  } else {
                    setSessionTimeoutError(null)
                  }
                  onChange({ sessionTimeoutMinutes: Math.min(1440, Math.max(1, v)) })
                }
              }}
            />
          </div>
          {sessionTimeoutError && (
            <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 2, marginBottom: 4 }}>
              {sessionTimeoutError}
            </div>
          )}
      </Card>}

      {/* Response Controls */}
      <Card title={t('guardrailsTab.responseControls.title')} subtitle={t('guardrailsTab.responseControls.subtitle')} className="mb-16">
          <SectionHeading label={t('guardrailsTab.sections.scope')} />

          <ToggleRow
            checked={form.restrictToAgentScope}
            onChange={(v) => onChange({ restrictToAgentScope: v })}
            label={t('guardrailsTab.toggles.restrictToScope')}
          >
            {t('guardrailsTab.toggles.restrictToScope')}{' '}
            <InfoTooltip>{t('guardrailsTab.toggles.restrictToScopeInfo')}</InfoTooltip>
          </ToggleRow>

          <Divider />
          <SectionHeading label={t('guardrailsTab.sections.internet')} />

          <ToggleRow
            checked={form.allowInternetAccess}
            onChange={(v) => onChange({ allowInternetAccess: v })}
            label={t('guardrailsTab.toggles.allowInternetAccess')}
          >
            {t('guardrailsTab.toggles.allowInternetAccess')}{' '}
            <InfoTooltip>{t('guardrailsTab.toggles.allowInternetAccessInfo')}</InfoTooltip>
          </ToggleRow>

          <Divider />
          <SectionHeading label={t('guardrailsTab.sections.citations')} />

          <ToggleRow
            checked={form.showConfidenceScores}
            onChange={(v) => onChange({ showConfidenceScores: v })}
            label={t('guardrailsTab.toggles.includeConfidence')}
          />
          <div className="toggle-row">
            <span className="toggle-row-label">{t('guardrailsTab.toggles.includeCitationsAlwaysOn')}</span>
            <Toggle
              checked={true}
              onChange={() => {}}
              label={t('guardrailsTab.toggles.includeCitationsAlwaysOn')}
              disabled
            />
          </div>

          {/* Formatting / response language — hidden for MVP (MS-562); set FEATURES.guardrailsResponseLanguage = true to re-enable */}
          {FEATURES.guardrailsResponseLanguage && (
            <>
              <Divider />
              <SectionHeading label={t('guardrailsTab.sections.formatting')} />

              {/* Response language */}
              <div className="guardrail-number" style={{ alignItems: 'flex-start' }}>
                <label htmlFor="response-language" style={{ paddingTop: 4 }}>
                  {t('guardrailsTab.fields.responseLanguage')}
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <select
                    id="response-language"
                    value={langSelectValue}
                    onChange={(e) => handleLangSelect(e.target.value)}
                    style={{ ...selectStyle, width: 200 }}
                  >
                    {CURATED_LANGUAGES.map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                    <option value="_other">{t('guardrailsTab.fields.customBcp47Option')}</option>
                  </select>
                  {showCustomInput && (
                    <div style={{ width: 200 }}>
                      <input
                        type="text"
                        aria-label={t('guardrailsTab.fields.customBcp47AriaLabel')}
                        placeholder={t('guardrailsTab.fields.customBcp47Placeholder')}
                        value={localCustomLang}
                        onChange={(e) => handleCustomLangChange(e.target.value)}
                        style={{
                          width: '100%',
                          height: 28,
                          padding: '0 8px',
                          fontSize: 13,
                          border: `1px solid ${customLangError ? 'var(--danger)' : 'var(--border)'}`,
                          borderRadius: 6,
                          background: 'var(--bg-input)',
                          color: 'var(--text-primary)',
                          boxSizing: 'border-box',
                          fontFamily: 'inherit',
                        }}
                      />
                      {customLangError && (
                        <div role="alert" style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                          {customLangError}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
      </Card>


      {/* dryRunMode off confirmation */}
      {dryRunConfirm && (
        <ConfirmDialog
          title={t('guardrailsTab.dryRunDialog.title')}
          message={t('guardrailsTab.dryRunDialog.message')}
          confirmLabel={t('guardrailsTab.dryRunDialog.confirm')}
          danger
          onConfirm={() => {
            setDryRunConfirm(false)
            onChange({ dryRunMode: false })
          }}
          onCancel={() => setDryRunConfirm(false)}
        />
      )}
    </>
  )
}
